import { useCallback, useMemo, useState } from 'react';
import type { DragEvent, MouseEvent } from 'react';
import { ActionIcon, Box, Center, Loader, Text } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { ReactFlowProvider, addEdge, useEdgesState, useNodesState, useReactFlow } from '@xyflow/react';
import type { Connection, Edge, EdgeChange, IsValidConnection, Node, NodeChange } from '@xyflow/react';
import { useMutation, useSubscription } from 'urql';
import { isUuid } from '@shared';
import { flowUsesElevenLabsVoice } from '@legacy-shared/widgets/core/voice';
import { defaultFlowVoiceConfig } from '@legacy-shared/widgets/core/voicePresets';
import type { FlowVoiceConfig, LanguageConfig } from '@legacy-shared/widgets/core/types';
import { DEV_SWITCHER_OFFSET } from '@/components/dev-user-switcher';
import { ErrorAlert } from '@/components/error-alert';
import { HandleActionProvider } from '@/components/flow-editor/handle-action-context';
import { useHandleRemeasure } from '@/hooks/use-handle-remeasure';
import { useMe } from '@/hooks/use-me';
import { useOrg } from '@/hooks/use-org';
import { useOrgWidgets } from '@/hooks/use-org-widgets';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { pruneHandleActions } from '@/lib/flow-editor/prune-handle-actions';
import { pruneLoadedFlow } from '@/lib/flow-editor/prune-loaded-flow';
import { pruneNodeEdges } from '@/lib/flow-editor/prune-orphaned-edges';
import { validateFlow } from '@/widgets/flow-validation';
import type { FlowIssue } from '@/widgets/flow-validation';
import '@/widgets/definitions';
import { widgetOutputs, widgetRegistry } from '@/widgets/registry';
import type { HandleAction, WidgetNodeData } from '@/widgets/types';
import { ConfigurationPanel } from './-components/configuration-panel';
import { FlowCanvas } from './-components/flow-canvas';
import { FlowEditorToolbar } from './-components/flow-editor-toolbar';
import { WidgetPalette } from './-components/widget-palette';
import {
  ElevenLabsAvailabilitySubscription,
  FlowSubscription,
  OrganizationCrmVariablesSubscription,
  UpdateFlowDraftMutation,
} from './-components/graphql';
import {
  readCrmVariables,
  readLanguageConfig,
  readStoredEdges,
  readStoredNodes,
  readVoiceConfig,
  toEditorEdges,
  toEditorNodes,
  toStoredEdges,
  toStoredNodes,
} from './-components/flow-data';

export const Route = createFileRoute('/_authed/flows/$flowId')({
  component: FlowEditorPage,
});

type LoadedFlow = {
  id: string;
  name: string;
  nodes: unknown;
  edges: unknown;
  voiceConfig: unknown;
  languageConfig: unknown;
  currentPublicationId: string | null;
};

function FlowEditorPage() {
  const { flowId } = Route.useParams();
  const { organizationId, isLoading: isOrgLoading } = useOrg();
  const hasValidId = isUuid(flowId);

  const [{ data, error }] = useSubscription({
    query: FlowSubscription,
    variables: { flowId },
    pause: !organizationId || !hasValidId,
  });

  if (isOrgLoading || (hasValidId && organizationId && data === undefined && !error)) {
    return (
      <Center h={`calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})`}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Box p="md">
        <ErrorAlert message={getErrorMessage(error, 'Failed to load flow')} />
      </Box>
    );
  }

  if (!organizationId) {
    return (
      <Center h={`calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})`}>
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  if (!data?.flowsByPk || data.flowsByPk.deletedAt !== null) {
    return (
      <Center h={`calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})`}>
        <Text c="dimmed">Flow not found</Text>
      </Center>
    );
  }

  return (
    <ReactFlowProvider>
      <FlowEditor flow={data.flowsByPk} organizationId={organizationId} />
    </ReactFlowProvider>
  );
}

function useCrmVariables(organizationId: string) {
  const { isSuperUser } = useMe();

  const [{ data }] = useSubscription({
    query: OrganizationCrmVariablesSubscription,
    variables: { organizationId },
    pause: !isSuperUser,
  });

  return useMemo(() => readCrmVariables(data?.organizationsByPk?.crmIntegration?.config), [data]);
}

function useElevenLabsAvailability() {
  const [{ data }] = useSubscription({ query: ElevenLabsAvailabilitySubscription });

  return (data?.elevenLabsVoicesAggregate.aggregate?.count ?? 0) > 0;
}

function FlowEditor({ flow, organizationId }: { flow: LoadedFlow; organizationId: string }) {
  const { screenToFlowPosition } = useReactFlow();
  const { enabledWidgetIds } = useOrgWidgets();
  const crmVariables = useCrmVariables(organizationId);
  const elevenLabsAvailable = useElevenLabsAvailability();
  const [, runUpdateDraft] = useMutation(UpdateFlowDraftMutation);

  const [isDirty, setIsDirty] = useState(false);

  const markChanged = useCallback(() => setIsDirty(true), []);

  const [loaded] = useState(() =>
    pruneLoadedFlow(
      toEditorNodes(readStoredNodes(flow.nodes)),
      toEditorEdges(readStoredEdges(flow.edges), markChanged),
      widgetOutputs,
    ),
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<WidgetNodeData>>(loaded.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(loaded.edges);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [configPanelOpen, setConfigPanelOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [prunedAtLoad, setPrunedAtLoad] = useState(loaded.prunedCount);
  const [voiceConfig, setVoiceConfig] = useState<FlowVoiceConfig>(
    readVoiceConfig(flow.voiceConfig) ?? { ...defaultFlowVoiceConfig },
  );
  const [languageConfig, setLanguageConfig] = useState<LanguageConfig | undefined>(
    readLanguageConfig(flow.languageConfig),
  );

  const hasChanges = isDirty || prunedAtLoad > 0;

  useHandleRemeasure(nodes, widgetOutputs);

  const selectedNode = useMemo(() => nodes.find((n) => n.id === selectedNodeId), [nodes, selectedNodeId]);

  const validation = useMemo(() => validateFlow(nodes, edges), [nodes, edges]);

  const issues = useMemo<FlowIssue[]>(
    () =>
      !elevenLabsAvailable && flowUsesElevenLabsVoice(voiceConfig, languageConfig)
        ? [
            ...validation.issues,
            { level: 'error', message: 'ElevenLabs voices are not available in this environment.' },
          ]
        : validation.issues,
    [validation.issues, elevenLabsAvailable, voiceConfig, languageConfig],
  );

  const errorCount = issues.filter((issue) => issue.level === 'error').length;
  const warningCount = issues.filter((issue) => issue.level === 'warning').length;

  const selectValidationNode = useCallback(
    (nodeId: string) => {
      setNodes((current) => current.map((n) => ({ ...n, selected: n.id === nodeId })));
      setSelectedNodeId(nodeId);
      setConfigPanelOpen(true);
    },
    [setNodes],
  );

  const handleNodesChange = useCallback(
    (changes: NodeChange<Node<WidgetNodeData>>[]) => {
      const allowed = changes.filter(
        (change) => change.type !== 'remove' || nodes.find((n) => n.id === change.id)?.type !== 'start',
      );

      onNodesChange(allowed);

      const selectChanges = allowed.filter((c) => c.type === 'select');
      const lastSelected = [...selectChanges].reverse().find((c) => c.selected);

      if (lastSelected) {
        setSelectedNodeId(lastSelected.id);
        setConfigPanelOpen(true);
      } else if (selectChanges.length > 0) {
        setSelectedNodeId((current) => (selectChanges.some((c) => c.id === current) ? null : current));
      }

      if (allowed.some((c) => c.type !== 'select' && c.type !== 'dimensions')) markChanged();
    },
    [onNodesChange, nodes, markChanged],
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      onEdgesChange(changes);

      if (changes.some((c) => c.type === 'add' || c.type === 'remove')) markChanged();
    },
    [onEdgesChange, markChanged],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((current) => addEdge({ ...connection, data: { onDelete: markChanged } }, current));
      markChanged();
    },
    [setEdges, markChanged],
  );

  const isValidConnection = useCallback<IsValidConnection>(
    (connection) => {
      if (connection.source === connection.target) return false;

      const sourceNode = nodes.find((n) => n.id === connection.source);

      if (!sourceNode?.type) return false;
      if (sourceNode.data.handleActions?.[connection.sourceHandle ?? '']) return false;
      if (edges.some((e) => e.source === connection.source && e.sourceHandle === connection.sourceHandle)) return false;

      const targetNode = nodes.find((n) => n.id === connection.target);

      if (!targetNode?.type) return false;

      const sourceWidget = widgetRegistry.get(sourceNode.type);
      const targetWidget = widgetRegistry.get(targetNode.type);

      if (!sourceWidget || !targetWidget) return false;

      const targetType = targetNode.type;
      const sourceType = sourceNode.type;

      return (
        (sourceWidget.connectionRules.allowedOutputTo.includes('*') ||
          sourceWidget.connectionRules.allowedOutputTo.includes(targetType)) &&
        (targetWidget.connectionRules.allowedInputFrom.includes('*') ||
          targetWidget.connectionRules.allowedInputFrom.includes(sourceType))
      );
    },
    [nodes, edges],
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const widget = type ? widgetRegistry.get(type) : undefined;

      if (!widget || widget.tier === 'system') return;

      const newNode: Node<WidgetNodeData> = {
        id: `${widget.id}-${Date.now()}`,
        type: widget.id,
        position: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
        selected: true,
        data: { config: { ...widget.defaultConfig }, configured: false },
      };

      setNodes((current) => [...current.map((n) => ({ ...n, selected: false })), newNode]);
      setSelectedNodeId(newNode.id);
      setConfigPanelOpen(true);
      markChanged();
    },
    [setNodes, screenToFlowPosition, markChanged],
  );

  const updateNodeConfig = useCallback(
    (nodeId: string, nodeType: string, config: Record<string, unknown>) => {
      const outputs = widgetOutputs(nodeType, config);

      setNodes((current) =>
        current.map((node) => {
          if (node.id !== nodeId) return node;

          const result = widgetRegistry.get(nodeType)?.validation.validateConfig(config);

          return {
            ...node,
            data: {
              ...node.data,
              config,
              configured: result?.valid ?? false,
              errors: result?.errors ?? [],
              handleActions: pruneHandleActions(node.data.handleActions, outputs).handleActions,
            },
          };
        }),
      );
      setEdges((current) => pruneNodeEdges(current, nodeId, outputs));
      markChanged();
    },
    [setNodes, setEdges, markChanged],
  );

  const updateNodeLabel = useCallback(
    (nodeId: string, label: string) => {
      setNodes((current) =>
        current.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, label: label || undefined } } : node,
        ),
      );
      markChanged();
    },
    [setNodes, markChanged],
  );

  const deleteNode = useCallback(
    (nodeId: string) => {
      setNodes((current) => current.filter((n) => n.id !== nodeId));
      setEdges((current) => current.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNodeId(null);
      markChanged();
    },
    [setNodes, setEdges, markChanged],
  );

  const setHandleAction = useCallback(
    (nodeId: string, handleId: string, action: HandleAction | undefined) => {
      if (action) setEdges((current) => current.filter((e) => !(e.source === nodeId && e.sourceHandle === handleId)));

      setNodes((current) =>
        current.map((node) => {
          if (node.id !== nodeId) return node;

          const handleActions = { ...node.data.handleActions };

          if (action) handleActions[handleId] = action;
          else delete handleActions[handleId];

          return {
            ...node,
            data: {
              ...node.data,
              handleActions: Object.keys(handleActions).length > 0 ? handleActions : undefined,
            },
          };
        }),
      );
      markChanged();
    },
    [setNodes, setEdges, markChanged],
  );

  const save = useCallback(async () => {
    setActionError(null);
    setIsSaving(true);

    const result = await runUpdateDraft({
      flowId: flow.id,
      nodes: toStoredNodes(nodes),
      edges: toStoredEdges(edges),
      entryNodeId: nodes[0]?.id ?? null,
      voiceConfig,
      languageConfig: languageConfig ?? null,
    });

    setIsSaving(false);

    if (result.error) {
      setActionError(getErrorMessage(result.error, 'Failed to save flow'));

      return false;
    }

    setIsDirty(false);
    setPrunedAtLoad(0);

    return true;
  }, [runUpdateDraft, flow.id, nodes, edges, voiceConfig, languageConfig]);

  const publish = useCallback(async () => {
    setActionError(null);

    if (hasChanges && !(await save())) return;

    setIsPublishing(true);

    const result = await api.flows({ flowId: flow.id }).publish.post();

    setIsPublishing(false);

    if (result.error) setActionError(getErrorMessage(result.error, 'Failed to publish flow'));
  }, [hasChanges, save, flow.id]);

  return (
    <HandleActionProvider nodes={nodes} onSetHandleAction={setHandleAction}>
      <Box style={{ display: 'flex', flexDirection: 'column', height: `calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})` }}>
        <FlowEditorToolbar
          flowId={flow.id}
          flowName={flow.name}
          currentPublicationId={flow.currentPublicationId}
          hasChanges={hasChanges}
          isSaving={isSaving}
          isPublishing={isPublishing}
          actionError={actionError}
          prunedAtLoad={prunedAtLoad}
          onSave={() => void save()}
          onPublish={() => void publish()}
          voiceConfig={voiceConfig}
          onVoiceConfigChange={(config) => {
            setVoiceConfig(config);
            markChanged();
          }}
          languageConfig={languageConfig}
          onLanguageConfigChange={(config) => {
            setLanguageConfig(config);
            markChanged();
          }}
          canPublish={errorCount === 0}
          issues={issues}
          errorCount={errorCount}
          warningCount={warningCount}
          onSelectNode={selectValidationNode}
          elevenLabsAvailable={elevenLabsAvailable}
        />

        <Box style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {paletteOpen && (
            <Box w={240} style={{ borderRight: '1px solid var(--mantine-color-default-border)' }}>
              <WidgetPalette enabledWidgets={enabledWidgetIds} />
            </Box>
          )}

          <Box style={{ flex: 1, position: 'relative' }}>
            <ActionIcon
              variant="subtle"
              size="sm"
              style={{ position: 'absolute', left: 8, top: 8, zIndex: 10 }}
              onClick={() => setPaletteOpen((open) => !open)}
            >
              {paletteOpen ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />}
            </ActionIcon>

            <FlowCanvas
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              isValidConnection={isValidConnection}
              onNodeClick={(_: MouseEvent, node: Node<WidgetNodeData>) => {
                setSelectedNodeId(node.id);
                setConfigPanelOpen(true);
              }}
              onPaneClick={() => setSelectedNodeId(null)}
              onDragOver={(event: DragEvent) => {
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
              }}
              onDrop={onDrop}
            />
          </Box>

          {configPanelOpen && selectedNode && (
            <Box w={320} style={{ borderLeft: '1px solid var(--mantine-color-default-border)' }}>
              <ConfigurationPanel
                key={selectedNode.id}
                node={selectedNode}
                nodes={nodes}
                edges={edges}
                onConfigChange={(config) => updateNodeConfig(selectedNode.id, selectedNode.type ?? '', config)}
                onLabelChange={(label) => updateNodeLabel(selectedNode.id, label)}
                onClose={() => setConfigPanelOpen(false)}
                onDelete={selectedNode.type !== 'start' ? () => deleteNode(selectedNode.id) : undefined}
                languageConfig={languageConfig}
                crmVariables={crmVariables}
              />
            </Box>
          )}
        </Box>
      </Box>
    </HandleActionProvider>
  );
}
